import React, { useState, useEffect, useMemo, useRef } from 'react';
import { jsPDF } from 'jspdf';
import JsBarcode from 'jsbarcode';
import api from '../services/api';
import BarcodeScanner from '../components/BarcodeScanner';
import { openPdfPreview } from '../utils/pdfGenerator';
import { generateLotNumber } from '../utils/lotNumberHelpers';
import { formatDate, calculateTotalWeight, calculateTotalCases } from '../utils/formatHelpers';
import {
  PalletsHeader,
  PalletsTable,
  PalletsCards,
  PalletModal,
  PalletWheelPicker
} from '../components/pallets';
import '../styles/Pallets.css';

const Pallets = () => {
  // === Data state ===
  const [pallets, setPallets] = useState([]);
  const [filteredPallets, setFilteredPallets] = useState([]);
  const [statusFilter, setStatusFilter] = useState('active');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [labelFilter, setLabelFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('palletsViewMode');
    if (saved) return saved;
    return window.innerWidth <= 1024 ? 'cards' : 'table';
  });

  // === Modal state ===
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedPallet, setSelectedPallet] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [restoreWheel, setRestoreWheel] = useState(true);
  const [pendingShipments, setPendingShipments] = useState([]);
  const [showCustomerChangeConfirm, setShowCustomerChangeConfirm] = useState(false);

  // === Form state ===
  const [palletItems, setPalletItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [saving, setSaving] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showItemScanner, setShowItemScanner] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const selectedCustomerIdRef = useRef('');

  // === Lot number state ===
  const [lotNumber, setLotNumber] = useState(generateLotNumber());
  const [lotDate, setLotDate] = useState(new Date().toISOString().split('T')[0]);

  // === Item edit state ===
  const [editingItemIndex, setEditingItemIndex] = useState(null);

  // === Refresh state ===
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // === Image state ===
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // === Label state ===
  const [labelText, setLabelText] = useState('');
  const [labelColor, setLabelColor] = useState('');
  const [isFull, setIsFull] = useState(false);
  const [notes, setNotes] = useState('');

  // === Wheel picker state ===
  const [wheels, setWheels] = useState([]);
  const [showWheelPicker, setShowWheelPicker] = useState(false);
  const [selectedWheelId, setSelectedWheelId] = useState(null);
  const [wheelSearch, setWheelSearch] = useState('');
  const [selectedWheelItems, setSelectedWheelItems] = useState([]);
  const [checkedWheelItems, setCheckedWheelItems] = useState({});
  const [loadingWheelItems, setLoadingWheelItems] = useState(false);

  // === Inventory items for search totals ===
  const [inventoryItems, setInventoryItems] = useState([]);

  // === Effects ===
  useEffect(() => {
    fetchPallets(true);
    fetchAllItems();
    fetchCustomers();
    fetchWheels();
    fetchInventoryItems();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchPallets();
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  useEffect(() => {
    selectedCustomerIdRef.current = selectedCustomerId;
  }, [selectedCustomerId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const palletId = params.get('id');
    const status = params.get('status');

    if (palletId && pallets.length > 0) {
      const pallet = pallets.find(p => p.id === parseInt(palletId));
      if (pallet) {
        handleEditPallet(pallet);
        window.history.replaceState({}, '', '/pallets');
      }
    }

    if (status && ['all', 'active', 'pending shipment', 'shipped'].includes(status)) {
      setStatusFilter(status);
      window.history.replaceState({}, '', '/pallets');
    }
  }, [pallets]);

  const labels = useMemo(() => {
    const seen = new Map();
    pallets.forEach(p => {
      if (p.label_text) {
        const key = p.label_text.trim().toLowerCase();
        if (!seen.has(key)) seen.set(key, p.label_text.trim());
      }
    });
    return Array.from(seen.values()).sort();
  }, [pallets]);

  useEffect(() => {
    if (labelFilter === 'all' || labelFilter === '__none__') return;
    const filterKey = labelFilter.trim().toLowerCase();
    const stillPresent = labels.some(l => l.trim().toLowerCase() === filterKey);
    if (!stillPresent) setLabelFilter('all');
  }, [labels, labelFilter]);

  useEffect(() => {
    let filtered = pallets;
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }
    if (customerFilter !== 'all') {
      filtered = filtered.filter(p => p.customer_id === parseInt(customerFilter));
    }
    if (labelFilter !== 'all') {
      if (labelFilter === '__none__') {
        filtered = filtered.filter(p => !p.label_text);
      } else {
        const filterKey = labelFilter.trim().toLowerCase();
        filtered = filtered.filter(p => p.label_text && p.label_text.trim().toLowerCase() === filterKey);
      }
    }
    if (dateFilter) {
      filtered = filtered.filter(p => {
        const palletDate = new Date(p.created_at).toISOString().split('T')[0];
        return palletDate === dateFilter;
      });
    }
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.pallet_number?.toLowerCase().includes(search) ||
        p.lot_number?.toLowerCase().includes(search) ||
        p.all_lot_numbers?.toLowerCase().includes(search) ||
        p.customer_name?.toLowerCase().includes(search) ||
        p.item_names?.toLowerCase().includes(search) ||
        p.label_text?.toLowerCase().includes(search) ||
        p.notes?.toLowerCase().includes(search)
      );
    }
    setFilteredPallets(filtered);
  }, [statusFilter, customerFilter, labelFilter, dateFilter, searchTerm, pallets]);

  // === API Calls ===
  const fetchPallets = async (showLoadingSpinner = false) => {
    try {
      if (showLoadingSpinner) setLoading(true);
      const response = await api.get('/pallets');
      setPallets(response.data);
      setFilteredPallets(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load pallets');
    } finally {
      if (showLoadingSpinner) setLoading(false);
    }
  };

  const handleManualRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    await fetchPallets();
    setTimeout(() => setIsRefreshing(false), 600);
  };

  const fetchAllItems = async () => {
    try {
      const response = await api.get('/items');
      setAllItems(response.data);
    } catch (err) {
      console.error('Failed to load items');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers?status=active');
      setCustomers(response.data);
    } catch (err) {
      console.error('Failed to load customers');
    }
  };

  const fetchWheels = async () => {
    try {
      const response = await api.get('/wheels');
      setWheels(response.data);
    } catch (err) {
      console.error('Failed to load wheels');
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const response = await api.get('/inventory/items');
      setInventoryItems(response.data);
    } catch (err) {
      console.error('Failed to load inventory items');
    }
  };

  // === Search totals calculation ===
  const searchTotals = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) return null;

    const search = searchTerm.toLowerCase();

    // Find items that match the search term
    const searchWords = search.trim().split(/\s+/);
    // Apply all active filters (status, customer, label) to match page view
    let filtered = inventoryItems;
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.pallet_status === statusFilter);
    }
    if (customerFilter !== 'all') {
      filtered = filtered.filter(item => item.customer_id === parseInt(customerFilter));
    }
    if (labelFilter !== 'all') {
      if (labelFilter === '__none__') {
        filtered = filtered.filter(item => !item.label_text);
      } else {
        const filterKey = labelFilter.trim().toLowerCase();
        filtered = filtered.filter(item => item.label_text && item.label_text.trim().toLowerCase() === filterKey);
      }
    }

    const matchingItems = filtered.filter(item => {
      const text = [item.item_name, item.part_number].join(' ').toLowerCase();
      const qtyStr = item.qty_per_case ? String(item.qty_per_case) : '';
      if (searchWords.length === 1) {
        return text.includes(searchWords[0]) || qtyStr === searchWords[0];
      }
      return searchWords.every(word => text.includes(word) || qtyStr === word);
    });

    if (matchingItems.length === 0) return null;

    // Group by item name and calculate totals
    const itemTotals = {};
    matchingItems.forEach(item => {
      const key = item.item_name;
      if (!itemTotals[key]) {
        itemTotals[key] = {
          name: item.item_name,
          partNumber: item.part_number,
          totalQty: 0,
          totalWeight: 0,
          palletCount: new Set()
        };
      }
      itemTotals[key].totalQty += parseFloat(item.quantity) || 0;
      itemTotals[key].totalWeight += parseFloat(item.subtotal_weight) || 0;
      itemTotals[key].palletCount.add(item.pallet_id);
    });

    // Convert to array and sort by quantity
    return Object.values(itemTotals)
      .map(item => ({
        ...item,
        palletCount: item.palletCount.size
      }))
      .sort((a, b) => b.totalQty - a.totalQty);
  }, [searchTerm, inventoryItems, statusFilter, customerFilter, labelFilter]);

  // === Filtered data ===
  const stripLeadingZeros = (str) => (str || '').replace(/^0+/, '') || '0';
  const searchNormalized = stripLeadingZeros(itemSearch.toLowerCase());

  const filteredItems = allItems.filter(item => {
    const text = [item.name, item.part_number].join(' ').toLowerCase();
    const barcode = (item.barcode || '').toLowerCase();
    const barcodeNorm = stripLeadingZeros(barcode);
    const qtyStr = item.qty_per_case ? String(item.qty_per_case) : '';
    const words = itemSearch.toLowerCase().trim().split(/\s+/);
    if (words.length === 1) {
      const w = words[0];
      return text.includes(w) || barcode.includes(w) || barcodeNorm.includes(stripLeadingZeros(w)) || qtyStr === w;
    }
    return words.every(word => text.includes(word) || qtyStr === word);
  });

  const filteredCustomers = customers.filter(customer =>
    customer.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    customer.city?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    customer.state?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const filteredWheels = wheels.filter(wheel =>
    wheel.wheel_number?.toLowerCase().includes(wheelSearch.toLowerCase()) ||
    wheel.customer_name?.toLowerCase().includes(wheelSearch.toLowerCase()) ||
    wheel.item_names?.toLowerCase().includes(wheelSearch.toLowerCase())
  );

  const customerDisplayString = (customerId, fallbackName) => {
    const customer = customers.find(c => c.id === parseInt(customerId));
    return customer ? `${customer.name} - ${customer.city}, ${customer.state}` : (fallbackName || '');
  };

  // === Modal Handlers ===
  const handleCreatePallet = () => {
    setPalletItems([]);
    setSelectedPallet(null);
    setSelectedCustomerId('');
    setCustomerSearch('');
    setShowCustomerDropdown(false);
    setPendingShipments([]);
    setShowCustomerChangeConfirm(false);
    setModalMode('create');
    setItemSearch('');
    setSelectedItemId('');
    setShowItemDropdown(false);
    setLabelText('');
    setLabelColor('');
    setIsFull(false);
    setNotes('');
    setLotNumber(generateLotNumber());
    setLotDate(new Date().toISOString().split('T')[0]);
    setEditingItemIndex(null);
    setShowModal(true);
  };

  const handleViewPallet = async (pallet) => {
    try {
      const response = await api.get(`/pallets/${pallet.id}`);
      setSelectedPallet(response.data);
      setSelectedCustomerId(response.data.customer_id || '');
      setCustomerSearch(customerDisplayString(response.data.customer_id, response.data.customer_name));
      setPendingShipments(response.data.pending_shipments || []);
      setPalletItems(response.data.items.map(item => ({
        item_id: item.item_id,
        name: item.name,
        part_number: item.part_number,
        quantity: item.quantity,
        case_weight: item.case_weight,
        subtotal_weight: item.subtotal_weight,
        lot_number: item.lot_number || generateLotNumber()
      })));
      setSelectedImage(null);
      setImagePreview(null);
      setLabelText(response.data.label_text || '');
      setLabelColor(response.data.label_color || '');
      setIsFull(response.data.is_full === 1);
      setNotes(response.data.notes || '');
      setModalMode('view');
      setShowModal(true);
    } catch (err) {
      alert('Failed to load pallet details');
    }
  };

  const handleEditPallet = async (pallet) => {
    try {
      const response = await api.get(`/pallets/${pallet.id}`);
      setSelectedPallet(response.data);
      setSelectedCustomerId(response.data.customer_id || '');
      setCustomerSearch(customerDisplayString(response.data.customer_id, response.data.customer_name));
      setPendingShipments(response.data.pending_shipments || []);
      setPalletItems(response.data.items.map(item => ({
        item_id: item.item_id,
        name: item.name,
        part_number: item.part_number,
        quantity: item.quantity,
        case_weight: item.case_weight,
        subtotal_weight: item.subtotal_weight,
        lot_number: item.lot_number || generateLotNumber()
      })));
      setSelectedImage(null);
      setImagePreview(null);
      setLabelText(response.data.label_text || '');
      setLabelColor(response.data.label_color || '');
      setIsFull(response.data.is_full === 1);
      setNotes(response.data.notes || '');
      setModalMode(pallet.status === 'shipped' ? 'view' : 'edit');
      setShowModal(true);
    } catch (err) {
      alert('Failed to load pallet details');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPallet(null);
    setPalletItems([]);
    setShowDeleteConfirm(false);
    setRestoreWheel(true);
    setSelectedImage(null);
    setImagePreview(null);
    setLabelText('');
    setLabelColor('');
    setIsFull(false);
    setNotes('');
    setEditingItemIndex(null);
    setPendingShipments([]);
    setShowCustomerChangeConfirm(false);
  };

  const handleCustomerFocus = () => {
    setCustomerSearch('');
    setShowCustomerDropdown(true);
  };

  const handleCustomerBlur = () => {
    setTimeout(() => {
      setShowCustomerDropdown(false);
      const currentId = selectedCustomerIdRef.current;
      if (currentId) {
        setCustomerSearch(customerDisplayString(currentId, selectedPallet?.customer_name));
      }
    }, 150);
  };

  // === Item Management ===
  const handleSelectItem = (item) => {
    setSelectedItemId(item.id);
    setItemSearch(`${item.part_number} - ${item.name}`);
    setShowItemDropdown(false);
  };

  const handleEditItem = (index) => {
    const item = palletItems[index];
    const matchedItem = allItems.find(i => i.id === item.item_id);
    if (!matchedItem) return;
    setEditingItemIndex(index);
    setSelectedItemId(matchedItem.id);
    setItemSearch(`${matchedItem.part_number} - ${matchedItem.name}`);
    setQuantity(item.quantity);
    setLotNumber(item.lot_number || generateLotNumber());
    setShowItemDropdown(false);
  };

  const handleCancelEditItem = () => {
    setEditingItemIndex(null);
    setSelectedItemId('');
    setItemSearch('');
    setQuantity(1);
    setLotNumber(generateLotNumber());
    setLotDate(new Date().toISOString().split('T')[0]);
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomerId(customer.id);
    setCustomerSearch(`${customer.name} - ${customer.city}, ${customer.state}`);
    setShowCustomerDropdown(false);
  };

  const handleAddItem = () => {
    if (!selectedItemId || quantity < 1) return;
    if (!lotNumber || lotNumber.length !== 4) {
      alert('Please enter a valid lot number (4 digits: DDDX)');
      return;
    }

    const item = allItems.find(i => i.id === parseInt(selectedItemId));
    if (!item) return;

    if (editingItemIndex !== null) {
      const updated = [...palletItems];
      updated[editingItemIndex] = {
        ...updated[editingItemIndex],
        item_id: item.id,
        name: item.name,
        part_number: item.part_number,
        quantity: quantity,
        case_weight: item.case_weight,
        subtotal_weight: (parseFloat(item.case_weight) || 0) * quantity,
        lot_number: lotNumber
      };
      setPalletItems(updated);
      setEditingItemIndex(null);
    } else {
      const existingIndex = palletItems.findIndex(pi => pi.item_id === item.id && pi.lot_number === lotNumber);
      if (existingIndex >= 0) {
        const updated = [...palletItems];
        updated[existingIndex].quantity += quantity;
        updated[existingIndex].subtotal_weight =
          (parseFloat(item.case_weight) || 0) * updated[existingIndex].quantity;
        setPalletItems(updated);
      } else {
        setPalletItems([...palletItems, {
          item_id: item.id,
          name: item.name,
          part_number: item.part_number,
          quantity: quantity,
          case_weight: item.case_weight,
          subtotal_weight: (parseFloat(item.case_weight) || 0) * quantity,
          lot_number: lotNumber
        }]);
      }
    }

    setSelectedItemId('');
    setItemSearch('');
    setQuantity(1);
    setLotNumber(generateLotNumber());
    setLotDate(new Date().toISOString().split('T')[0]);
  };

  const handleRemoveItem = (index) => {
    const updated = [...palletItems];
    updated.splice(index, 1);
    setPalletItems(updated);
  };

  const handleUpdateQuantity = (index, newQty) => {
    if (newQty !== '' && newQty < 1) return;
    const updated = [...palletItems];
    updated[index].quantity = newQty;
    updated[index].subtotal_weight =
      (parseFloat(updated[index].case_weight) || 0) * (parseInt(newQty) || 0);
    setPalletItems(updated);
  };

  // === Wheel Picker ===
  const handleSelectWheel = async (wheelId) => {
    setSelectedWheelId(wheelId);
    setLoadingWheelItems(true);
    try {
      const response = await api.get(`/wheels/${wheelId}`);
      const wheelData = response.data;
      setSelectedWheelItems(wheelData.items || []);
      const checked = {};
      (wheelData.items || []).forEach((item, index) => {
        checked[index] = true;
      });
      setCheckedWheelItems(checked);
    } catch (err) {
      alert('Failed to load wheel items');
      setSelectedWheelItems([]);
      setCheckedWheelItems({});
    } finally {
      setLoadingWheelItems(false);
    }
  };

  const handleToggleWheelItem = (index) => {
    setCheckedWheelItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleToggleAllWheelItems = (selectAll) => {
    const checked = {};
    selectedWheelItems.forEach((item, index) => {
      checked[index] = selectAll;
    });
    setCheckedWheelItems(checked);
  };

  const handlePickFromWheel = async () => {
    if (!selectedWheelId || selectedWheelItems.length === 0) return;

    const itemsToAdd = selectedWheelItems.filter((item, index) => checkedWheelItems[index]);
    if (itemsToAdd.length === 0) {
      alert('Please select at least one item');
      return;
    }

    try {
      const newItems = itemsToAdd.map(item => ({
        item_id: item.item_id,
        name: item.name,
        part_number: item.part_number,
        quantity: item.quantity,
        case_weight: item.case_weight,
        subtotal_weight: item.subtotal_weight,
        lot_number: item.lot_number
      }));

      const existingItems = [...palletItems];
      newItems.forEach(newItem => {
        const existingIndex = existingItems.findIndex(
          pi => pi.item_id === newItem.item_id && pi.lot_number === newItem.lot_number
        );
        if (existingIndex >= 0) {
          existingItems[existingIndex].quantity += newItem.quantity;
          existingItems[existingIndex].subtotal_weight =
            (parseFloat(existingItems[existingIndex].case_weight) || 0) * existingItems[existingIndex].quantity;
        } else {
          existingItems.push(newItem);
        }
      });
      setPalletItems(existingItems);

      const wheelData = wheels.find(w => w.id === selectedWheelId);
      if (!selectedCustomerId && wheelData?.customer_id) {
        setSelectedCustomerId(wheelData.customer_id);
        const customer = customers.find(c => c.id === wheelData.customer_id);
        if (customer) {
          setCustomerSearch(`${customer.name} - ${customer.city}, ${customer.state}`);
        }
      }

      try {
        const allItemsPicked = selectedWheelItems.every((item, index) => checkedWheelItems[index]);
        if (allItemsPicked) {
          await api.delete(`/wheels/${selectedWheelId}`);
        } else {
          const remainingItems = selectedWheelItems
            .filter((item, index) => !checkedWheelItems[index])
            .map(item => ({
              item_id: item.item_id,
              quantity: item.quantity,
              subtotal_weight: item.subtotal_weight,
              lot_number: item.lot_number
            }));

          await api.put(`/wheels/${selectedWheelId}`, {
            customer_id: wheelData?.customer_id || null,
            lot_number: wheelData?.lot_number || '',
            label_text: wheelData?.label_text || null,
            label_color: wheelData?.label_color || null,
            notes: wheelData?.notes || null,
            items: remainingItems
          });
        }
      } catch (cleanupErr) {
        console.warn('Wheel cleanup failed (items still added to pallet):', cleanupErr);
      }
      fetchWheels();

      setShowWheelPicker(false);
      setSelectedWheelId(null);
      setSelectedWheelItems([]);
      setCheckedWheelItems({});
      setWheelSearch('');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to pick from wheel');
    }
  };

  // === Save/Delete ===
  const handleSavePallet = () => {
    if (palletItems.length === 0) {
      alert('Please add at least one item to the pallet');
      return;
    }

    if (!selectedCustomerId) {
      alert('Please select a customer');
      return;
    }

    if (
      modalMode === 'edit' &&
      selectedCustomerId !== selectedPallet?.customer_id &&
      pendingShipments.length > 0
    ) {
      setShowCustomerChangeConfirm(true);
      return;
    }

    savePallet();
  };

  const savePallet = async () => {
    setShowCustomerChangeConfirm(false);
    setSaving(true);
    try {
      const payload = {
        customer_id: selectedCustomerId,
        label_text: labelText || null,
        label_color: labelColor || null,
        is_full: isFull,
        notes: notes || null,
        items: palletItems.map(item => ({
          item_id: item.item_id,
          quantity: item.quantity,
          lot_number: item.lot_number
        }))
      };

      if (modalMode === 'create') {
        const response = await api.post('/pallets', payload);
        const newPalletId = response.data.id;
        const palletResponse = await api.get(`/pallets/${newPalletId}`);
        const newPallet = palletResponse.data;
        setSelectedPallet(newPallet);
        setPalletItems(newPallet.items || []);
        setModalMode('view');
        fetchPallets();
      } else {
        await api.put(`/pallets/${selectedPallet.id}`, payload);
        const palletResponse = await api.get(`/pallets/${selectedPallet.id}`);
        const updatedPallet = palletResponse.data;
        setSelectedPallet(updatedPallet);
        setPalletItems(updatedPallet.items || []);
        setModalMode('view');
        fetchPallets();
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save pallet');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePallet = async () => {
    try {
      const params = selectedPallet?.source_wheel_id && restoreWheel
        ? '?restore_wheel=true'
        : '';
      await api.delete(`/pallets/${selectedPallet.id}${params}`);
      setShowDeleteConfirm(false);
      setShowModal(false);
      setRestoreWheel(true);
      fetchPallets();
      fetchWheels();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete pallet');
    }
  };

  // === View Mode ===
  const toggleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('palletsViewMode', mode);
  };

  // === Scanning ===
  const handleScan = () => {
    setShowScanner(true);
  };

  const handleBarcodeScan = (barcode) => {
    setSearchTerm(barcode);
    setShowScanner(false);
  };

  const handleItemBarcodeScan = (barcode) => {
    setShowItemScanner(false);
    const scannedLower = barcode.toLowerCase().trim();
    const scannedNormalized = stripLeadingZeros(scannedLower);

    const item = allItems.find(i => {
      const partLower = i.part_number?.toLowerCase() || '';
      const barcodeLower = i.barcode?.toLowerCase() || '';
      const barcodeNormalized = stripLeadingZeros(barcodeLower);

      // Match exact or with leading zeros stripped
      return partLower === scannedLower ||
             barcodeLower === scannedLower ||
             barcodeNormalized === scannedNormalized;
    });

    if (item) {
      setSelectedItemId(item.id);
      setItemSearch(`${item.part_number} - ${item.name}`);
      setShowItemDropdown(false);
    } else {
      // Put barcode in search field to show filtered results
      setItemSearch(barcode);
      setShowItemDropdown(true);
    }
  };

  // === PDF Generation ===
  const generatePalletLabel = () => {
    const palletNumber = selectedPallet?.pallet_number || 'NEW';
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let y = 15;

    // Header
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Montreal Kosher', 15, y);
    y += 6;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('PALLET LABEL', 15, y);

    doc.setFontSize(48);
    doc.setFont('helvetica', 'bold');
    doc.text(palletNumber, pageWidth - 15, 22, { align: 'right' });

    y += 12;

    if (selectedPallet?.customer_name) {
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(`Customer: ${selectedPallet.customer_name}`, 15, y);
    }

    let badgeX = pageWidth - 15;

    if (selectedPallet?.is_full) {
      doc.setFillColor(211, 47, 47);
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      const fullWidth = doc.getTextWidth('FULL') + 12;
      doc.roundedRect(badgeX - fullWidth, y - 5, fullWidth, 8, 2, 2, 'F');
      doc.text('FULL', badgeX - fullWidth/2, y, { align: 'center' });
      badgeX -= fullWidth + 5;
      doc.setTextColor(0, 0, 0);
    }

    if (selectedPallet?.label_text && selectedPallet?.label_color) {
      const colorMap = {
        red: [231, 76, 60], blue: [52, 152, 219], green: [39, 174, 96],
        orange: [243, 156, 18], purple: [155, 89, 182], yellow: [241, 196, 15],
        pink: [233, 30, 99], gray: [149, 165, 166]
      };
      const rgb = colorMap[selectedPallet.label_color] || [149, 165, 166];
      doc.setFillColor(rgb[0], rgb[1], rgb[2]);
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      const textWidth = doc.getTextWidth(selectedPallet.label_text);
      const badgeWidth = textWidth + 12;
      doc.roundedRect(badgeX - badgeWidth, y - 5, badgeWidth, 8, 2, 2, 'F');
      doc.text(selectedPallet.label_text, badgeX - badgeWidth/2, y, { align: 'center' });
      doc.setTextColor(0, 0, 0);
    }

    y += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Date: ${formatDate(selectedPallet?.created_at || new Date())}`, 15, y);

    y += 8;

    // Notes removed from pallet label - they now appear on the Pallet List PDF in Shipments

    y += 5;

    doc.setDrawColor(139, 28, 36);
    doc.setLineWidth(1);
    doc.line(15, y, pageWidth - 15, y);
    y += 10;

    doc.setFillColor(139, 28, 36);
    doc.rect(15, y - 6, pageWidth - 30, 10, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('ITEM', 20, y);
    doc.text('LOT', 130, y);
    doc.text('QTY', 160, y);
    doc.text('KG', 185, y);
    y += 10;

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);

    const maxItemsY = pageHeight - 70;

    palletItems.forEach((item, index) => {
      if (y > maxItemsY) {
        doc.addPage();
        y = 20;
      }
      if (index % 2 === 0) {
        doc.setFillColor(240, 240, 240);
        doc.rect(15, y - 6, pageWidth - 30, 10, 'F');
      }
      const name = item.name.length > 50 ? item.name.substring(0, 47) + '...' : item.name;
      doc.text(name, 20, y);
      doc.text(item.lot_number || '-', 130, y);
      doc.text(`${item.quantity}`, 162, y);
      doc.text(`${parseFloat(item.subtotal_weight || 0).toFixed(1)}`, 185, y);
      y += 10;
    });
    y += 5;

    const totalCases = calculateTotalCases(palletItems);
    const totalWeight = calculateTotalWeight(palletItems).toFixed(1);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(1);
    doc.line(15, y, pageWidth - 15, y);
    y += 8;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`TOTAL: ${palletItems.length} items`, 20, y);
    doc.text(`${totalCases}`, 162, y);
    doc.text(`${totalWeight} kg`, 180, y);

    const barcodeY = pageHeight - 45;
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, palletNumber, {
      format: 'CODE128',
      width: 3,
      height: 80,
      displayValue: false
    });
    const barcodeDataUrl = canvas.toDataURL('image/png');

    const barcodeWidth = 120;
    const barcodeHeight = 35;
    doc.addImage(barcodeDataUrl, 'PNG', (pageWidth - barcodeWidth) / 2, barcodeY, barcodeWidth, barcodeHeight);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(palletNumber, pageWidth / 2, barcodeY + barcodeHeight + 8, { align: 'center' });

    openPdfPreview(doc, `Pallet-${selectedPallet?.pallet_number || 'Label'}.pdf`);
  };

  const generatePalletNumberPrint = () => {
    const palletNumber = selectedPallet?.pallet_number || 'NEW';
    const doc = new jsPDF({ orientation: 'landscape' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(180);

    let fontSize = 180;
    let textWidth = doc.getTextWidth(palletNumber);
    while (textWidth > pageWidth - 30 && fontSize > 40) {
      fontSize -= 10;
      doc.setFontSize(fontSize);
      textWidth = doc.getTextWidth(palletNumber);
    }

    doc.text(palletNumber, pageWidth / 2, pageHeight / 2 + fontSize * 0.12, { align: 'center' });

    openPdfPreview(doc, `PalletNumber-${palletNumber}.pdf`);
  };

  // === Render ===
  if (loading) {
    return (
      <div className="pallets-page">
        <div className="loading">Loading pallets...</div>
      </div>
    );
  }

  return (
    <div className="pallets-page">
      <PalletsHeader
        viewMode={viewMode}
        onToggleView={toggleViewMode}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onScan={handleScan}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        customerFilter={customerFilter}
        onCustomerFilterChange={setCustomerFilter}
        customers={customers}
        labelFilter={labelFilter}
        onLabelFilterChange={setLabelFilter}
        labels={labels}
        dateFilter={dateFilter}
        onDateFilterChange={setDateFilter}
        filteredCount={filteredPallets.length}
        onCreateClick={handleCreatePallet}
        searchTotals={searchTotals}
        onRefresh={handleManualRefresh}
        isRefreshing={isRefreshing}
        autoRefresh={autoRefresh}
        onAutoRefreshChange={setAutoRefresh}
      />

      {error && <div className="error-message">{error}</div>}

      {viewMode === 'table' ? (
        <PalletsTable
          pallets={filteredPallets}
          onView={handleViewPallet}
          onEdit={handleEditPallet}
        />
      ) : (
        <PalletsCards
          pallets={filteredPallets}
          onView={handleViewPallet}
          onEdit={handleEditPallet}
        />
      )}

      <PalletModal
        isOpen={showModal}
        mode={modalMode}
        pallet={selectedPallet}
        palletItems={palletItems}
        customers={customers}
        allItems={allItems}
        selectedCustomerId={selectedCustomerId}
        onCustomerChange={setSelectedCustomerId}
        customerSearch={customerSearch}
        onCustomerSearchChange={setCustomerSearch}
        showCustomerDropdown={showCustomerDropdown}
        onShowCustomerDropdown={setShowCustomerDropdown}
        filteredCustomers={filteredCustomers}
        onSelectCustomer={handleSelectCustomer}
        onCustomerFocus={handleCustomerFocus}
        onCustomerBlur={handleCustomerBlur}
        itemSearch={itemSearch}
        onItemSearchChange={setItemSearch}
        showItemDropdown={showItemDropdown}
        onShowItemDropdown={setShowItemDropdown}
        filteredItems={filteredItems}
        onSelectItem={handleSelectItem}
        selectedItemId={selectedItemId}
        quantity={quantity}
        onQuantityChange={setQuantity}
        lotNumber={lotNumber}
        onLotNumberChange={setLotNumber}
        lotDate={lotDate}
        onLotDateChange={setLotDate}
        onAddItem={handleAddItem}
        onRemoveItem={handleRemoveItem}
        onUpdateQuantity={handleUpdateQuantity}
        editingItemIndex={editingItemIndex}
        onEditItem={handleEditItem}
        onCancelEditItem={handleCancelEditItem}
        labelText={labelText}
        onLabelTextChange={setLabelText}
        labelColor={labelColor}
        onLabelColorChange={setLabelColor}
        isFull={isFull}
        onIsFullChange={setIsFull}
        notes={notes}
        onNotesChange={setNotes}
        onSave={handleSavePallet}
        onDelete={handleDeletePallet}
        onEdit={() => {
          setSelectedCustomerId(selectedPallet?.customer_id || '');
          setCustomerSearch(customerDisplayString(selectedPallet?.customer_id, selectedPallet?.customer_name));
          setModalMode('edit');
        }}
        onClose={closeModal}
        saving={saving}
        showDeleteConfirm={showDeleteConfirm}
        onShowDeleteConfirm={() => setShowDeleteConfirm(true)}
        onCancelDelete={() => setShowDeleteConfirm(false)}
        onConfirmDelete={handleDeletePallet}
        pendingShipments={pendingShipments}
        showCustomerChangeConfirm={showCustomerChangeConfirm}
        onCancelCustomerChange={() => setShowCustomerChangeConfirm(false)}
        onConfirmCustomerChange={savePallet}
        wheels={wheels}
        onOpenWheelPicker={() => setShowWheelPicker(true)}
        onScanItem={() => setShowItemScanner(true)}
        onGenerateLabel={generatePalletLabel}
        onGeneratePrintNumber={generatePalletNumberPrint}
        imagePreview={imagePreview}
        uploadingImage={uploadingImage}
        restoreWheel={restoreWheel}
        onRestoreWheelChange={setRestoreWheel}
      />

      <PalletWheelPicker
        isOpen={showWheelPicker}
        onClose={() => {
          setShowWheelPicker(false);
          setSelectedWheelId(null);
          setSelectedWheelItems([]);
          setCheckedWheelItems({});
          setWheelSearch('');
        }}
        wheels={wheels}
        wheelSearch={wheelSearch}
        onWheelSearchChange={setWheelSearch}
        filteredWheels={filteredWheels}
        selectedWheelId={selectedWheelId}
        onSelectWheel={handleSelectWheel}
        selectedWheelItems={selectedWheelItems}
        checkedWheelItems={checkedWheelItems}
        onToggleWheelItem={handleToggleWheelItem}
        onToggleAllWheelItems={handleToggleAllWheelItems}
        loadingWheelItems={loadingWheelItems}
        onPickItems={handlePickFromWheel}
        onBack={() => {
          setSelectedWheelId(null);
          setSelectedWheelItems([]);
          setCheckedWheelItems({});
        }}
      />

      <BarcodeScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleBarcodeScan}
        title="Scan Pallet Barcode"
      />

      <BarcodeScanner
        isOpen={showItemScanner}
        onClose={() => setShowItemScanner(false)}
        onScan={handleItemBarcodeScan}
        title="Scan Item Barcode"
      />
    </div>
  );
};

export default Pallets;
